<template>
  <div class="flex flex-col items-center justify-center h-full space-y-2.5 my-auto px-3 !pt-0">
    <div
      class="flex flex-col p-6 w-full md:w-3/4 lg:w-1/2 mx-auto border shadow-xl rounded-lg 2xl:w-1/3 bg-white"
    >
      <div class="flex items-center space-x-10 justify-center">
        <img
          src="/images/logo-clear.png"
          class="w-32"
        >

        <div>
          <h1 class="text-2xl font-semibold break-words w-min my-auto">
            Construction Commercial Management
          </h1>

          <p class="font-bold text-ccm-blue-500">
            <span class="font-bold text-[#bb6101]">Get Control</span>

            <br class="sm:hidden">

            <span class="font-bold text-ccm-blue-500">
              Keep Control
            </span>
          </p>
        </div>
      </div>

      <form
        class="flex flex-col space-y-6 w-4/5 mx-auto mt-6"
        @submit.prevent="submitForm"
      >
        <h2 class="text-2xl md:text-3xl font-semibold break-words text-center text-gray-800">
          {{ $t('Login.title') }}
        </h2>

        <label>
          {{ $t('Login.label.email') }}
          <AppInput
            v-model="loginForm.form.email"
            name="email"
            type="text"
            :error="loginForm.getErrors('email')"
            :disabled="loggingIn"
            data-test="email"
            class="form-input"
            required
          />
        </label>

        <label>
          {{ $t('Login.label.password') }}
          <AppInput
            v-model="loginForm.form.password"
            name="password"
            type="password"
            :error="loginForm.getErrors('password')"
            :disabled="loggingIn"
            data-test="password"
            class="form-input"
            required
          />
        </label>

        <button
          v-if="!loggingIn"
          type="submit"
          class="w-full btn btn--primary-blue"
          data-test="login"
        >
          <span>
            {{ $t('Login.title') }}
          </span>
        </button>

        <div
          v-else
          class="flex justify-center"
        >
          <AppSpinner
            class="fill-white"
          />
        </div>
      </form>

      <div class="flex justify-between items-center mt-10">
        <div class="flex items-center space-x-2">
          <label class="text-sm text-gray-600">{{ $t('Login.language') }}:</label>

          <AppLanguageSelector />
        </div>

        <RouterLink
          :to="{ name: 'ResetPassword'}"
          class="btn btn--tertiary-blue"
        >
          {{ $t('Login.forgotPassword') }}
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import useScreenSize, { ScreenSize } from '@/composables/useScreenSize';
import { useCompaniesStore } from '@/store/companies';
import { useNavStore } from '@/store/nav';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { ref } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const loggingIn = ref(false);
const userStore = useUserStore();
const projectsStore = useProjectsStore();
const companiesStore = useCompaniesStore();

const loginForm = useFormData({
  email: '',
  password: '',
});

const { screenSize } = useScreenSize();

const submitForm = async () => {
  loggingIn.value = true;
  loginForm.resetErrors();

  userStore.signIn(loginForm.form.email, loginForm.form.password).then(async () => {
    companiesStore.index();

    const availableProjects = await projectsStore.fetchProjects();
    const unarchivedProjects = availableProjects.filter((project) => !project.archived_at);
    let project = unarchivedProjects[0];

    if(project === undefined) {
      project = availableProjects[0];
    }

    if(!project) {
      router.push({ name: 'ProjectSelector' });

      return;
    }

    projectsStore.setProject(project.id, false);

    if(screenSize.value < ScreenSize.MD) {
      if(userStore.intendedRoute) {
        router.push(userStore.intendedRoute);
      } else {
        router.push('/mobile/projects');
      }
    } else if(userStore.isAdmin) {
      window.location.href = '/admin';
    } else {
      if(userStore.intendedRoute) {
        router.push(userStore.intendedRoute);
      } else {
        router.push(`/home`);
      }
    }
  }).catch((error) => {
    loggingIn.value = false;
    loginForm.setErrors(error?.data);
  });
};

const navStore = useNavStore();

navStore.backRoute = undefined;
</script>

<style scoped></style>
